import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatCrowns, formatBigNum } from '../util/formatBig.js';
import type {
  CombatRoundDto,
  ReplayDetailDto,
  ReplaySummaryDto,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

/**
 * Replays page — also handles deep-link to a specific replay via
 * `#/replays?id=<id>` from the Profile activity feed or leaderboards.
 */
export async function renderReplays(root: HTMLElement): Promise<void> {
  // Pull replay id from query string if present.
  let activeId: string | null = null;
  if (window.location.hash.includes('?')) {
    const q = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
    activeId = q.get('id');
  }

  let list: ReplaySummaryDto[] = [];
  let detail: ReplayDetailDto | null = null;
  let flash: FlashState | null = null;
  const myCharId = store.get().character?.id ?? '';

  async function refreshList(): Promise<void> {
    try {
      const r = await api.listReplays(30);
      list = r.replays;
    } catch (err) {
      flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed to load replays.' };
    }
  }

  async function loadDetail(id: string): Promise<void> {
    try {
      const r = await api.getReplay(id);
      detail = r.replay;
    } catch (err) {
      detail = null;
      flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Failed to load replay.' };
    }
  }

  await refreshList();
  if (activeId) await loadDetail(activeId);
  else if (list.length > 0) {
    activeId = list[0]!.id;
    await loadDetail(activeId);
  }

  mountShell(root, (main) => {
    const renderMain = (): void => {
      main.innerHTML = `
        <img class="page-banner" src="/img/banners/replays.webp" alt="" />
        <h1>The Annals — Replays</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Every blow remembered. Click a fight to relive it round-by-round.
        </p>

        <div id="flash"></div>

        <div class="replay-layout">
          <aside class="replay-list">
            ${list.length === 0
              ? '<div class="mail-empty">No fights logged yet. Go pick one in the Sparring Hall or the Attacks page.</div>'
              : list.map((r) => renderListRow(r, myCharId, activeId)).join('')}
          </aside>
          <section class="replay-detail">
            ${detail ? renderDetail(detail, myCharId) : '<p style="font-style:italic; color: var(--c-ink-soft);">Select a fight on the left to view.</p>'}
          </section>
        </div>
      `;

      const style = document.createElement('style');
      style.textContent = STYLES;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      // Wire row clicks.
      main.querySelectorAll<HTMLButtonElement>('button[data-replay-id]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.replayId!;
          activeId = id;
          // Update the URL so this is shareable / refresh-safe.
          const newHash = `#/replays?id=${encodeURIComponent(id)}`;
          if (window.location.hash !== newHash) {
            history.replaceState(null, '', newHash);
          }
          await loadDetail(id);
          anchored(renderMain);
        });
      });
    };

    anchored(renderMain);
  });
}

function renderListRow(r: ReplaySummaryDto, myId: string, activeId: string | null): string {
  const youAttacked = r.attackerId === myId;
  const outcomeForYou = youAttacked
    ? r.outcome
    : r.outcome === 'WIN' ? 'LOSS'
    : r.outcome === 'LOSS' ? 'WIN'
    : r.outcome;

  const cls = outcomeForYou === 'WIN' ? 'win' : outcomeForYou === 'LOSS' ? 'loss' : 'draw';
  const youLabel = youAttacked ? `vs ${escapeHtml(r.defenderName)}` : `from ${escapeHtml(r.attackerName)}`;
  const when = new Date(r.attackedAt).toLocaleString();
  const isActive = activeId === r.id;

  return `
    <button type="button" class="replay-row ${cls} ${isActive ? 'active' : ''}" data-replay-id="${r.id}">
      <div class="row-head">
        <span class="outcome">${outcomeForYou}</span>
        <span class="when">${escapeHtml(when)}</span>
      </div>
      <div class="row-name">${youLabel}</div>
      <div class="row-meta">${r.rounds} round${r.rounds === 1 ? '' : 's'} · ${r.damageDealt} dmg dealt · ${r.damageReceived} taken</div>
    </button>
  `;
}

function renderDetail(d: ReplayDetailDto, myId: string): string {
  const youAttacked = d.attackerId === myId;
  const outcomeBanner = youAttacked
    ? d.outcome
    : d.outcome === 'WIN' ? 'LOSS' : d.outcome === 'LOSS' ? 'WIN' : d.outcome;
  const bannerCls = outcomeBanner === 'WIN' ? 'win' : outcomeBanner === 'LOSS' ? 'loss' : 'draw';

  const rounds = d.roundsData ?? [];
  const roundsHtml = rounds.length === 0
    ? '<p style="font-style:italic; color: var(--c-ink-soft);">No round detail recorded for this fight (logged before replays existed).</p>'
    : rounds.map((rnd, idx) => renderRound(rnd as CombatRoundDto, idx, youAttacked)).join('');

  const when = new Date(d.attackedAt).toLocaleString();

  // Attacker is always a player; defender is linkable only when it's a character (not an NPC).
  const attackerHtml = `<a href="#/u/${encodeURIComponent(d.attackerId)}">${escapeHtml(d.attackerName)}</a>`;
  const defenderHtml =
    d.defenderType === 'CHARACTER'
      ? `<a href="#/u/${encodeURIComponent(d.defenderRefId)}">${escapeHtml(d.defenderName)}</a>`
      : escapeHtml(d.defenderName);

  return `
    <div class="detail-banner ${bannerCls}">${outcomeBanner}</div>
    <div class="detail-head">
      <h2>${attackerHtml} <span class="vs">vs</span> ${defenderHtml}</h2>
      <div class="detail-meta">${escapeHtml(when)} · ${d.rounds} round${d.rounds === 1 ? '' : 's'} · ${d.damageDealt}/${d.damageReceived} dmg · ${formatBigNum(d.xpGained)} xp · +${formatCrowns(d.crownsGained)} crowns</div>
    </div>
    <div class="rounds-list">
      ${roundsHtml}
    </div>
  `;
}

interface SideSwing {
  hit?: boolean;
  damage?: number;
  lifeAfter?: number;
  crit?: boolean;
}

interface RoundLike {
  index?: number;
  attacker?: SideSwing;
  defender?: SideSwing;
  firstActor?: 'attacker' | 'defender';
}

function renderRound(round: CombatRoundDto, idx: number, youAttacked: boolean): string {
  // Combat round JSON shape: { index, attacker: {hit, damage, lifeAfter, crit?}, defender: {...}, firstActor }
  const r = round as unknown as RoundLike;
  const num = r.index ?? idx + 1;
  const youSide: 'attacker' | 'defender' = youAttacked ? 'attacker' : 'defender';

  const swing = (who: 'attacker' | 'defender'): string => {
    const s: SideSwing = (who === 'attacker' ? r.attacker : r.defender) ?? {};
    const youDoing = who === youSide;
    const label = youDoing ? 'You' : (who === 'attacker' ? 'Foe' : 'Foe');
    if (s.hit === false) return `<span class="miss">${label} missed</span>`;
    const dmg = s.damage ?? 0;
    return `<span class="hit ${s.crit ? 'crit' : ''}">${label} ${s.crit ? 'CRIT' : 'struck'} for <strong>${dmg}</strong></span>`;
  };

  const first = r.firstActor ?? 'attacker';
  const second: 'attacker' | 'defender' = first === 'attacker' ? 'defender' : 'attacker';

  const aLife = r.attacker?.lifeAfter;
  const dLife = r.defender?.lifeAfter;

  return `
    <div class="round">
      <div class="round-num">Round ${num}</div>
      <div class="round-swings">
        ${swing(first)}
        ${swing(second)}
      </div>
      ${typeof aLife === 'number' && typeof dLife === 'number' ? `
        <div class="round-hp">
          HP — ${youAttacked ? 'you' : 'foe'}: ${aLife} · ${youAttacked ? 'foe' : 'you'}: ${dLife}
        </div>
      ` : ''}
    </div>
  `;
}

const STYLES = `
  .replay-layout {
    display: grid;
    grid-template-columns: 320px 1fr;
    gap: var(--s-3);
    margin-top: var(--s-3);
  }
  @media (max-width: 720px) {
    .replay-layout { grid-template-columns: 1fr; }
  }
  .replay-list {
    background: var(--c-parchment);
    background-image: var(--parchment-texture);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    padding: var(--s-2);
    max-height: 70vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: var(--s-2);
  }
  .replay-row {
    text-align: left;
    background: rgba(var(--c-surface-tint-strong), 0.65);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    padding: var(--s-2) var(--s-3);
    cursor: pointer;
    font: inherit;
    color: var(--c-ink);
    transition: border-color 0.12s ease, background 0.12s ease;
  }
  .replay-row:hover { border-color: var(--c-rule-strong); }
  .replay-row.active { border-color: var(--c-gold); box-shadow: 0 0 0 1px var(--c-gold); }
  .replay-row .row-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-family: var(--font-display);
    font-size: 0.9rem;
  }
  .replay-row .outcome {
    font-weight: 700;
    letter-spacing: 0.06em;
  }
  .replay-row.win .outcome { color: var(--c-success); }
  .replay-row.loss .outcome { color: var(--c-danger); }
  .replay-row.draw .outcome { color: var(--c-ink-soft); }
  .replay-row .when { font-size: 0.78rem; color: var(--c-ink-soft); }
  .replay-row .row-name {
    font-family: var(--font-elegant);
    font-style: italic;
    font-size: 1rem;
    margin: 2px 0;
  }
  .replay-row .row-meta {
    font-family: var(--font-numeric);
    font-size: 0.85rem;
    color: var(--c-ink-soft);
  }
  .replay-detail {
    background: var(--c-parchment);
    background-image: var(--parchment-texture);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    padding: var(--s-4);
  }
  .detail-banner {
    display: inline-block;
    font-family: var(--font-display);
    letter-spacing: 0.1em;
    font-weight: 700;
    padding: 4px 14px;
    border-radius: 999px;
    margin-bottom: var(--s-3);
  }
  .detail-banner.win  { background: rgba(63, 107, 44, 0.18); color: var(--c-success); border: 1px solid var(--c-success); }
  .detail-banner.loss { background: rgba(138, 28, 28, 0.18); color: var(--c-danger);  border: 1px solid var(--c-danger); }
  .detail-banner.draw { background: rgba(122, 85, 32, 0.12); color: var(--c-ink-soft); border: 1px solid var(--c-rule); }
  .detail-head h2 { margin: 0 0 4px 0; }
  .detail-head .vs { font-family: var(--font-elegant); color: var(--c-ink-soft); font-style: italic; }
  .detail-meta {
    font-family: var(--font-display);
    font-size: 0.95rem;
    color: var(--c-ink-soft);
    margin-bottom: var(--s-3);
  }
  .rounds-list { display: flex; flex-direction: column; gap: var(--s-2); }
  .round {
    background: rgba(var(--c-surface-tint-strong), 0.55);
    border: 1px solid var(--c-rule);
    border-radius: var(--radius);
    padding: var(--s-2) var(--s-3);
  }
  .round-num {
    font-family: var(--font-display);
    font-weight: 700;
    color: var(--c-oxblood);
    letter-spacing: 0.04em;
    margin-bottom: 4px;
  }
  .round-swings {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-family: var(--font-body);
    font-size: 0.95rem;
    color: var(--c-ink);
  }
  .round-swings .hit strong { color: var(--c-oxblood); }
  .round-swings .hit.crit strong { color: var(--c-gold-text); }
  .round-swings .miss { font-style: italic; color: var(--c-ink-soft); }
  .round-hp {
    margin-top: 4px;
    font-family: var(--font-numeric);
    font-size: 0.85rem;
    color: var(--c-ink-soft);
  }
`;
